import { Decimal } from 'decimal.js';

import { Offer } from 'modules/shared-menu/pages/SharedMenuEditorPage/types';
import { OfferWithSelectionAndAuthor } from 'modules/shared-menu/types';

import { ValidPriceChangeFormValues } from './validation';
import { getCurrencyMaxDecimals } from '../../../OfferDetailsForm/utils';

const MINIMUM_PRICE = 0;

type AppliedDiscountType = Extract<
  ValidPriceChangeFormValues['type'],
  'changeByDiscountAmount' | 'changeByDiscountPercentage'
>;

export const getPriceChangeType = (
  type: ValidPriceChangeFormValues['type'],
): 'absolute' | 'relative' | 'relative-percentage' => {
  switch (type) {
    case 'singlePrice':
      return 'absolute';
    case 'changeByAmount':
      return 'relative';
    case 'changeByPercentage':
      return 'relative-percentage';
    default:
      throw new Error(`No matching case for ${type}`);
  }
};

export const getPriceChangeAmount = (validFormValues: ValidPriceChangeFormValues): Decimal => {
  switch (validFormValues.type) {
    case 'changeByAmount':
      return new Decimal(Number(validFormValues.changeAmount)).times(validFormValues.operation === 'addition' ? 1 : -1);
    case 'changeByPercentage':
      return new Decimal(Number(validFormValues.changePercentage)).times(
        validFormValues.operation === 'addition' ? 1 : -1,
      );
    default:
      return new Decimal(Number(validFormValues.singlePrice));
  }
};

export const getAppliedDiscountType = (type: ValidPriceChangeFormValues['type']) => {
  switch (type) {
    case 'changeByDiscountAmount':
      return 'absolute';
    case 'changeByDiscountPercentage':
      return 'relative';
    default:
      // type check for this case happens in buildBulkActionUpdatePricePayload
      // but TS is not able to narrow it on its own
      throw new Error(`No matching case for ${type}`);
  }
};

export const getAppliedDiscountAmount = (validFormValues: ValidPriceChangeFormValues): Decimal => {
  switch (validFormValues.type) {
    case 'changeByDiscountAmount':
      return new Decimal(Number(validFormValues.applyDiscountAmount)).absoluteValue();
    case 'changeByDiscountPercentage':
      return new Decimal(Number(validFormValues.applyDiscountPercentage)).absoluteValue();
    default:
      // type check for this case happens in buildBulkActionUpdatePricePayload
      // but TS is not able to narrow it on its own
      throw new Error(`No matching case for ${validFormValues.type}`);
  }
};

export const isDiscountChangeType = (type: ValidPriceChangeFormValues['type']): type is AppliedDiscountType =>
  ['changeByDiscountAmount', 'changeByDiscountPercentage'].includes(type);

type OfferPriceAttrs = Pick<Offer, 'price' | 'discounted'>;

export const applyOfferDiscountChange = (
  originalOffer: Offer,
  validValues: ValidPriceChangeFormValues,
  maxDecimals: number,
): OfferPriceAttrs => {
  const originalPrice = originalOffer.discounted ? originalOffer.discounted.original_price : originalOffer.price;

  const discountAmount = getAppliedDiscountAmount(validValues);
  const discountType = getAppliedDiscountType(validValues.type);

  const suggestedChangeAmount =
    discountType === 'absolute' ? discountAmount : new Decimal(originalPrice).times(discountAmount.div(100));
  const newPrice = new Decimal(originalPrice).minus(suggestedChangeAmount).toDecimalPlaces(maxDecimals).toNumber();

  return {
    price: newPrice >= MINIMUM_PRICE ? newPrice : MINIMUM_PRICE,
    discounted: {
      original_price: originalPrice,
      discount_type: discountType,
    },
  };
};

export const applyOfferPriceChange = (
  originalOffer: Offer,
  validValues: ValidPriceChangeFormValues,
  maxDecimals: number,
): OfferPriceAttrs => {
  const originalPrice = originalOffer.price;

  const suggestedChangeAmount =
    validValues.type === 'changeByAmount'
      ? getPriceChangeAmount(validValues)
      : new Decimal(originalPrice).times(getPriceChangeAmount(validValues).div(100));
  const suggestedNewPrice =
    validValues.type === 'singlePrice'
      ? Number(validValues.singlePrice)
      : new Decimal(originalPrice).plus(suggestedChangeAmount).toDecimalPlaces(maxDecimals).toNumber();

  const offerIsDiscountedAndSuggestedPriceIsHigherThanOriginalPrice =
    originalOffer.discounted && suggestedNewPrice >= originalOffer.discounted.original_price;

  const newPrice = offerIsDiscountedAndSuggestedPriceIsHigherThanOriginalPrice ? originalPrice : suggestedNewPrice;

  return {
    price: newPrice >= MINIMUM_PRICE ? newPrice : MINIMUM_PRICE,
    discounted: originalOffer.discounted,
  };
};

export const buildBatchPriceChangeOfferPayloads = (
  validValues: ValidPriceChangeFormValues,
  offers: Offer[],
  selectionId: string,
  userId: string,
  currency: Currency,
): OfferWithSelectionAndAuthor[] => {
  const maxDecimals = getCurrencyMaxDecimals(currency);
  return offers.map(offer => {
    const { scope, ...offerWithoutScope } = offer;

    const changedAttrs = isDiscountChangeType(validValues.type)
      ? applyOfferDiscountChange(offer, validValues, maxDecimals)
      : applyOfferPriceChange(offer, validValues, maxDecimals);

    return {
      ...offerWithoutScope,
      ...changedAttrs,
      selection_id: selectionId,
      author: {
        kind: 'user',
        id: userId,
      },
    };
  });
};
